// src/components/TemplateSelector.jsx
import { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { sendTemplate } from '../services/templateService';

/**
 * Extraer variables del template
 * Retorna objeto con variables de body y botones
 * Body: Variables en formato {{1}}, {{2}} en el contenido principal
 * Buttons: Variables en formato [button:1], [button:2] en el contenido
 */
function extractVariables(content) {
  // Variables de body {{1}}, {{2}}
  const bodyRegex = /\{\{(\d+)\}\}/g;
  const bodyVariables = new Set();
  let match;

  while ((match = bodyRegex.exec(content)) !== null) {
    bodyVariables.add(match[1]);
  }

  // Variables de botones [button:1], [button:2]
  const buttonRegex = /\[button:(\d+)\]/g;
  const buttonVariables = new Set();

  while ((match = buttonRegex.exec(content)) !== null) {
    buttonVariables.add(match[1]);
  }

  return {
    body: Array.from(bodyVariables).sort((a, b) => Number(a) - Number(b)),
    buttons: Array.from(buttonVariables).sort((a, b) => Number(a) - Number(b))
  };
}

export default function TemplateSelector({ conversationId, disabled = false, templates = [], loading = false }) {
  const { t } = useTranslation();

  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [bodyVariableValues, setBodyVariableValues] = useState({});
  const [buttonVariableValues, setButtonVariableValues] = useState({});

  // Extraer variables cuando se selecciona un template
  const templateVariables = useMemo(() => {
    if (!selectedTemplate) return [];
    return extractVariables(selectedTemplate.content);
  }, [selectedTemplate]);

  // Reemplazar variables en el contenido para preview
  const previewContent = useMemo(() => {
    if (!selectedTemplate) return '';

    let content = selectedTemplate.content;

    // Reemplazar variables de body
    templateVariables.body.forEach(varNum => {
      const value = bodyVariableValues[varNum] || `{{${varNum}}}`;
      content = content.replace(new RegExp(`\\{\\{${varNum}\\}\\}`, 'g'), value);
    });

    // Reemplazar variables de botones
    templateVariables.buttons.forEach(varNum => {
      const value = buttonVariableValues[varNum] || `[button:${varNum}]`;
      content = content.replace(new RegExp(`\\[button:${varNum}\\]`, 'g'), `[BUTTON: ${value}]`);
    });

    return content;
  }, [selectedTemplate, templateVariables, bodyVariableValues, buttonVariableValues]);

  const handleTemplateClick = (template) => {
    setSelectedTemplate(template);
    setBodyVariableValues({}); // Reset body variables
    setButtonVariableValues({}); // Reset button variables
    setError('');
    setPreviewOpen(true);
  };

  const handleBodyVariableChange = (varNum, value) => {
    setBodyVariableValues(prev => ({
      ...prev,
      [varNum]: value
    }));
  };

  const handleButtonVariableChange = (varNum, value) => {
    setButtonVariableValues(prev => ({
      ...prev,
      [varNum]: value
    }));
  };

  const handleSendTemplate = async () => {
    if (!selectedTemplate) return;

    // Validar que todas las variables de body tengan valor
    const missingBodyVars = templateVariables.body.filter(varNum => !bodyVariableValues[varNum]?.trim());
    if (missingBodyVars.length > 0) {
      setError(`Faltan variables del mensaje: ${missingBodyVars.join(', ')}`);
      return;
    }

    // Validar que todas las variables de botones tengan valor
    const missingButtonVars = templateVariables.buttons.filter(varNum => !buttonVariableValues[varNum]?.trim());
    if (missingButtonVars.length > 0) {
      setError(`Faltan variables de botones: ${missingButtonVars.join(', ')}`);
      return;
    }

    try {
      setSending(true);
      setError('');

      // Combinar variables de body y botones en el formato esperado por el backend
      const variables = {
        body: bodyVariableValues,
        buttons: buttonVariableValues
      };

      await sendTemplate(conversationId, selectedTemplate.id, variables);
      setPreviewOpen(false);
      setSelectedTemplate(null);
      setBodyVariableValues({});
      setButtonVariableValues({});
    } catch (err) {
      console.error('[TemplateSelector] Error sending template:', err);
      setError(t('templates.error.send') || 'Error enviando plantilla');
    } finally {
      setSending(false);
    }
  };

  const previewModal = previewOpen && selectedTemplate && createPortal(
    <div
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50"
      onClick={() => setPreviewOpen(false)}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-5xl p-6 relative max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <button
          onClick={() => setPreviewOpen(false)}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 text-2xl z-10"
        >
          ✕
        </button>

        <h2 className="text-2xl font-semibold mb-6">
          {t('templates.preview.title') || 'Vista previa de plantilla'}
        </h2>

        <div className="flex gap-6">
          {/* Panel izquierdo: Variables */}
          {(templateVariables.body.length > 0 || templateVariables.buttons.length > 0) && (
            <div className="w-1/3 border-r pr-6">
              <h3 className="text-lg font-semibold text-gray-700 mb-4">
                {t('templates.variables.title') || 'Variables'}
              </h3>
              <div className="space-y-6">
                {/* Variables del mensaje (body) */}
                {templateVariables.body.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-600 mb-3">
                      📝 Variables del mensaje
                    </h4>
                    <div className="space-y-3">
                      {templateVariables.body.map(varNum => (
                        <div key={`body-${varNum}`}>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Variable {varNum}
                          </label>
                          <input
                            type="text"
                            value={bodyVariableValues[varNum] || ''}
                            onChange={(e) => handleBodyVariableChange(varNum, e.target.value)}
                            placeholder={`Valor para {{${varNum}}}`}
                            className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Variables de botones */}
                {templateVariables.buttons.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-600 mb-3">
                      🔘 Variables de botones
                    </h4>
                    <div className="space-y-3">
                      {templateVariables.buttons.map(varNum => (
                        <div key={`button-${varNum}`}>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Botón {varNum}
                          </label>
                          <input
                            type="text"
                            value={buttonVariableValues[varNum] || ''}
                            onChange={(e) => handleButtonVariableChange(varNum, e.target.value)}
                            placeholder={`Valor para botón ${varNum}`}
                            className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Este valor se usará en el URL del botón
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Panel derecho: Preview */}
          <div className={(templateVariables.body.length > 0 || templateVariables.buttons.length > 0) ? 'w-2/3' : 'w-full'}>
            <div className="mb-4">
              <h3 className="text-sm font-semibold text-gray-600 mb-2">
                {t('templates.preview.name') || 'Nombre'}
              </h3>
              <p className="text-lg font-medium">{selectedTemplate.name}</p>
            </div>

            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-600 mb-2">
                {t('templates.preview.content') || 'Vista previa'}
              </h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 min-h-[200px]">
                <p className="whitespace-pre-wrap">{previewContent}</p>
              </div>
              {(templateVariables.body.length > 0 || templateVariables.buttons.length > 0) && (
                <p className="text-xs text-gray-500 mt-2">
                  {t('templates.preview.hint') || 'Completa las variables en el panel izquierdo para ver el mensaje final'}
                </p>
              )}
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700">
                {error}
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setPreviewOpen(false)}
                className="px-4 py-2 rounded border border-gray-300 hover:bg-gray-100"
                disabled={sending}
              >
                {t('common.cancel') || 'Cancelar'}
              </button>
              <button
                onClick={handleSendTemplate}
                className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:bg-gray-400"
                disabled={sending}
              >
                {sending ? (t('templates.sending') || 'Enviando...') : (t('templates.send') || 'Enviar plantilla')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );

  if (loading) {
    return (
      <div className="text-center py-4 text-gray-500">
        {t('common.loading') || 'Cargando...'}
      </div>
    );
  }

  return (
    <>
      <div className="mb-4">
        <h4 className="text-sm font-semibold text-gray-700 mb-2">
          {t('templates.title') || 'Plantillas'}
        </h4>

        {templates.length === 0 ? (
          <p className="text-sm text-gray-500">
            {t('templates.empty') || 'No hay plantillas disponibles'}
          </p>
        ) : (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {templates.map(template => (
              <button
                key={template.id}
                onClick={() => !disabled && handleTemplateClick(template)}
                disabled={disabled}
                className={`w-full text-left px-3 py-2 bg-white border border-gray-300 rounded transition-colors ${
                  disabled
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:bg-blue-50 hover:border-blue-400 cursor-pointer'
                }`}
              >
                <div className="text-sm font-medium text-gray-800">
                  {template.name}
                </div>
                <div className="text-xs text-gray-500 truncate mt-1">
                  {template.content.substring(0, 50)}...
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {previewModal}
    </>
  );
}
